<template>
	<div class="w-full">
		<!-- Header -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary mb-2 tracking-tight"
			>
				Your Invoices
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				Manage and track your insurance invoices
			</p>
		</div>

		<!-- Header Actions Section -->
		<div class="flex flex-wrap gap-2 sm:gap-4 mb-6">
			<ViewInvoice />
			<InvoicePayment />
			<RequestInvoiceFinance />
		</div>

		<!-- Invoices Grid -->
		<div
			v-if="selectInvoicesData.length"
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
		>
			<InvoiceItem
				v-for="(invoice, i) in selectInvoicesData"
				:key="i"
				:invoice="invoice"
				@click.native="fetchInvoiceDetails(invoice)"
				class="group block bg-white rounded-lg border border-border hover:border-primary-light hover:shadow-md transition-all duration-300 cursor-pointer"
				:style="{ animationDelay: `${i * 0.1}s` }"
			/>
		</div>

		<!-- Empty State -->
		<div
			v-else-if="!loading"
			class="text-center py-12 bg-white border border-border rounded-lg"
		>
			<div
				class="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-surface mb-3"
			>
				<svg
					class="w-7 h-7 text-text-secondary"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
					/>
				</svg>
			</div>
			<h3
				class="text-base lg:text-lg font-semibold text-text-primary mb-1"
			>
				No invoices found
			</h3>
			<p class="text-xs lg:text-sm text-text-secondary mb-4">
				Your invoices will appear here once created.
			</p>
			<button
				class="inline-flex items-center px-4 py-2 text-xs lg:text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
			>
				<svg
					class="w-4 h-4 mr-1.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 4v16m8-8H4"
					/>
				</svg>
				Create Invoice
			</button>
		</div>

		<!-- Loading State -->
		<div
			v-if="loading"
			class="bg-white rounded-lg border border-border p-6 text-center"
		>
			<div
				class="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
			>
				<div
					class="w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"
				></div>
				<span
					class="text-sm sm:text-base text-text-secondary font-medium"
					>Loading invoices...</span
				>
			</div>
		</div>

		<!-- Pagination -->
		<div v-if="selectInvoicesData.length" class="mt-6">
			<Pagination @page-change="fetchClientInvoices" />
		</div>
	</div>
</template>

<script>
import { FetchClientInvoices, FetchInvoiceDetails } from '../api/portal';
import entityInvoices from '../rsc/stores/client-invoice.store';
import { getPageNumbers } from '@/helpers/pagination';
import { mapGetters, mapActions } from 'vuex';

export default {
	name: 'Invoices',
	beforeCreate() {
		Event.$emit('layout:loading:start', 'Loading invoices...');
	},
	components: {
		Advert: () => import('../templates/ads/advert'),
		InvoiceItem: () => import('../templates/others/invoice-item'),
		ViewInvoice: () => import('./view-invoice'),
		InvoicePayment: () => import('./invoice-payment.vue'),
		Pagination: () => import('../templates/others/pagination.vue'),
		RequestInvoiceFinance: () => import('./request-invoice-finance.vue'),
	},
	beforeDestroy() {
		this.$store.unregisterModule('entityInvoices');
	},
	created() {
		this.$store.registerModule('entityInvoices', entityInvoices);
	},
	computed: {
		...mapGetters(['loading', 'invoices']),
		...mapGetters('invoices', ['selectInvoicesData']),
		...mapGetters('pagination', ['currentPage']),
	},
	data() {
		return {
			utils: {
				registerModule: (name, module, store) =>
					store.registerModule(name, module),
			},
			products: [],
			accounts: [],
		};
	},
	methods: {
		...mapActions('invoices', [
			'setInvoiceDetails',
			'setClientInvoicesData',
		]),
		...mapActions('pagination', ['setPagesArray', 'setTotalPages']),

		async fetchClientInvoices() {
			try {
				const fetchData = {
					querryOptions: { page: this.currentPage },
				};
				const res = await FetchClientInvoices(fetchData);
				if (res.status) {
					if (res.data.length > 0) {
						this.setClientInvoicesData(res.data[0].items || []);

						const pages = getPageNumbers(res.data[0].pageCount, 20);
						this.setPagesArray(pages);
						this.setTotalPages(res.data[0].totalCount);
					}
				}
			} catch (error) {
				console.error('Error fetching invoices:', error);
				this.handleApiError(error, 'Failed to load invoices');
			}
		},

		async fetchInvoiceDetails(invoice) {
			const ref = {
				invoiceSystemRef: invoice.invoiceSystemRef,
			};
			try {
				const res = await FetchInvoiceDetails(ref);
				if (res.status) {
					console.log('invoicedata', res.data);
					this.setInvoiceDetails(res.data);
					this.$store.commit('showInvoice', true);
				}
			} catch (err) {
				console.error('Error fetching invoice details:', err);
				this.handleApiError(err, 'Failed to load invoice details');
			}
		},

		handleApiError(error, defaultMessage) {
			const errorMessage =
				(error.response &&
					error.response.data &&
					error.response.data.errors) ||
				(error.response &&
					error.response.data &&
					error.response.data.message) ||
				error.message ||
				defaultMessage;
			Event.$emit('notifications:show', {
				type: 'error',
				title: 'Error',
				message: errorMessage,
				duration: 5000,
			});
		},
	},
	async mounted() {
		Event.$emit('layout:loading:stop');
		await this.fetchClientInvoices();
	},
};
</script>

<style scoped>
/* Animation for grid items */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.grid > * {
	animation: fadeInUp 0.5s ease-out;
}

/* Focus styles for accessibility */
button:focus-visible,
a:focus-visible {
	outline: 2px solid theme('colors.primary.DEFAULT');
	outline-offset: 2px;
	border-radius: 0.375rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
	.text-3xl {
		font-size: 1.875rem;
		line-height: 2.25rem;
	}
}

@media (max-width: 640px) {
	.cursor-pointer {
		min-height: 44px;
	}
}

/* Print styles */
@media print {
	.bg-background {
		background: white !important;
	}
	.shadow-lg,
	.shadow-sm {
		box-shadow: none !important;
	}
	.hover\:shadow-lg:hover,
	.hover\:-translate-y-1:hover {
		box-shadow: none !important;
		transform: none !important;
	}
	.grid {
		grid-template-columns: repeat(3, 1fr) !important;
		gap: 1rem !important;
	}
}

/* High contrast support */
@media (prefers-contrast: high) {
	.border-border {
		border-color: #000;
	}
	.text-text-primary {
		color: #000;
	}
	.text-text-secondary {
		color: #333;
	}
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
	.transition-all,
	.animate-spin,
	.hover\:-translate-y-1:hover {
		transition: none !important;
		animation: none !important;
		transform: none !important;
	}
}
</style>
